import {
	CollectionExportPolicy,
	EXISTING_VARIANT_GROUP,
	LongRunningTaskStatus,
	variantSelectionKey,
} from '@ikegps/core-api';
import { IkeToken, type VariantExportSelection } from '@ikegps/core-api';
import { action, makeObservable, observable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { MenuComponent } from '../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../components/menu/menu.item';
import { IMithrilComponent } from '../../components/mithril.component';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services/index';
import { CurrentState } from '../../state/page.state';
import { distinctVariantTypes, hasCollectionVariants } from '../collection.list/collection.variant.group';
import {
	type ExportTypeOption,
	ExportVariantsModal,
	type VariantCheckboxOption,
} from '../export.variants.modal/export.variants.modal';
import { getLongRunningTaskManager, type LongRunningExport } from '../long.running.task.manager';
import { ReportTypeEnum } from '../report/ReportMaker';
import { viewExportError } from '../export.notification/export.notification';
import { ExportMenuController } from './export.menu.ctrl';

interface ExportTypeEntry {
	labelKey: T;
	visible: boolean;
	action: () => any;
	id?: string;
	testId?: string;
	/** When present, the "Export type" modal uses this (with the checked variant selections) instead of `action`. */
	variantAction?: (selections: VariantExportSelection[]) => any;
	/** When true, the "Export type" modal greys out every "Include variants" checkbox but Existing. */
	disableVariants?: boolean;
}

export class ExportMenu extends IMithrilComponent {
	menu: MenuComponent;
	variantsModal: ExportVariantsModal;

	/** Legacy mode: spinner state while an export is in progress */
	@observable private exporting = false;

	constructor() {
		super();
		makeObservable(this);
		this.menu = new MenuComponent({
			items: () => this.viewDownloadItems(),
		});
		this.variantsModal = new ExportVariantsModal(
			() => this.visibleExportTypeOptions(),
			() => this.variantCheckboxOptions()
		);
	}

	private get legacyMode(): boolean {
		return !Services.Token.has(IkeToken.FEATURE_NEW_EXPORT_UI);
	}

	/**
	 * When the user has the export-variants token and the selection includes a
	 * pole with variants, the download button opens the "Export Collections" modal (with an
	 * "Include variants" picker) instead of the usual export-type dropdown.
	 */
	private get showVariantsModal(): boolean {
		return (
			Services.Token.has(IkeToken.FEATURE_EXPORT_VARIANTS) && CurrentState.Tag.collections.some(hasCollectionVariants)
		);
	}

	private showExportMenu = (event: MouseEvent) => {
		if (this.showVariantsModal) {
			this.variantsModal.open();
			return;
		}
		this.menu.show(event);
	};

	get nodes(): MithrilVNode {
		if (this.exporting || !CurrentState.Tag.ready) {
			return Spinner.view();
		}

		const userProfile = Services.UserProfile.get(Services.User.current.objectId);

		if (!Services.UserProfile.canExportReports(userProfile)) {
			return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE_PERMISSION,
				disabled: true,
			});
		}

		if (CurrentState.CollectionEdit.isDirty) {
			return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE_DIRTY,
				disabled: true,
			});
		}

		if (CurrentState.Tag.collections.length > CollectionExportPolicy.MAX_LIMIT) {
			return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE_LIMIT,
				titleAttrs: { limit: CollectionExportPolicy.MAX_LIMIT },
				disabled: true,
			});
		}

		return m('div', [
			ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE,
				onclick: this.showExportMenu,
				id: 'export-menu',
			}),
			this.menu.view(),
		]);
	}

	/** The download items, shared between the dropdown menu and the "Export type" modal select. */
	private exportTypeEntries(): ExportTypeEntry[] {
		const oneCollectionSelected = CurrentState.Tag.collections.length === 1;
		const tagsSelected = Boolean(CurrentState.Tag.tags.length);
		const singleJobSelected = CurrentState.Tag.tags.length === 1;
		const canReportShort = Services.Token.has(IkeToken.FEATURE_REPORT_SHORT);
		const hasReportCompositePhoto =
			Services.Token.has(IkeToken.FEATURE_REPORT_COMPOSITE_PHOTO) ||
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		const hasReportCompositePhotoOnly = Services.Token.has(IkeToken.FEATURE_REPORT_COMPOSITE_ONLY);
		const hasReportPhoto =
			Services.Token.has(IkeToken.FEATURE_REPORT_PHOTO) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		// This ensures that all collections have the same form revision which is required for importing excel
		const canExportImportableExcel =
			CurrentState.Tag.collections.every(
				(collectionSummery) => collectionSummery.formRevision === CurrentState.Tag.collections[0].formRevision
			) && Services.Token.hasAll([IkeToken.FEATURE_IMPORT_CSV, IkeToken.FEATURE_IMPORT_EXCEL]);

		const run = (func: () => Promise<string | string[] | void>) => {
			if (this.legacyMode) {
				return this.runExportLegacy(func);
			}
			return ExportMenuController.runExport(func);
		};

		const silent = this.legacyMode;

		return [
			{
				visible: tagsSelected,
				labelKey: T.EXPORT_KML,
				action: () => run(() => ExportMenuController.exportKML({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportKMLVariants(selections, { silent })),
				testId: 'export-kml',
			},
			{
				visible: tagsSelected,
				labelKey: T.EXCEL,
				action: () => run(() => ExportMenuController.exportExcel({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportExcelVariants(selections, { silent })),
				testId: 'export-excel',
			},
			{
				visible: canExportImportableExcel,
				labelKey: T.IMPORTABLE_EXCEL,
				action: () => run(() => ExportMenuController.exportImportableExcel({ silent })),
				// Importable Excel expects the plain selected collections, one row per pole — it can't
				// represent variants, so only the "Existing" checkbox makes sense.
				disableVariants: true,
			},
			{
				visible: tagsSelected,
				labelKey: T.EXPORT_JSON,
				action: () => run(() => ExportMenuController.exportJSON({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportJSONVariants(selections, { silent })),
				testId: 'export-json',
			},
			{
				visible: singleJobSelected && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE),
				labelKey: T.EXPORT_SHAPEFILE,
				action: () => run(() => ExportMenuController.exportShapefile({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportShapefile({ silent }, selections)),
				id: 'export-shapefile',
			},
			this.reportEntry(ReportTypeEnum.ReportType, T.EXPORT_REPORT, tagsSelected, 'export-report-ike'),
			this.reportEntry(
				ReportTypeEnum.ReportShortType,
				T.EXPORT_REPORT_SHORT,
				tagsSelected && canReportShort,
				'export-report-short'
			),
			this.reportEntry(
				ReportTypeEnum.ReportCompositePhotoType,
				T.EXPORT_REPORT_COMPOSITE_PHOTO,
				tagsSelected && hasReportCompositePhoto,
				'export-report-composite-photo'
			),
			this.reportEntry(
				ReportTypeEnum.ReportCompositePhotoOnlyType,
				T.EXPORT_REPORT_COMPOSITE_PHOTO_ONLY,
				tagsSelected && hasReportCompositePhotoOnly,
				'export-report-composite-photo-only'
			),
			this.reportEntry(
				ReportTypeEnum.ReportPhotoType,
				T.EXPORT_REPORT_PHOTO,
				tagsSelected && hasReportPhoto,
				'export-report-photo'
			),
			{
				visible: tagsSelected && ExportMenuController.hasOCalcForm(CurrentState.Tag.collections),
				labelKey: T.EXPORT_O_CALC,
				action: () => run(() => ExportMenuController.exportOCalc({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportOCalc({ silent }, selections)),
			},
			{
				visible: tagsSelected && ExportMenuController.hasSPIDACalcForm(CurrentState.Tag.collections),
				labelKey: T.EXPORT_SPIDA_CALC,
				action: () => run(() => ExportMenuController.exportSPIDACalc(false, { silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportSPIDACalc(false, { silent }, selections)),
				testId: 'export-spidacalc',
			},
			{
				visible: tagsSelected && ExportMenuController.hasSPIDACalcForm(CurrentState.Tag.collections),
				labelKey: T.EXPORT_SPIDA_CALC_NO_PHOTO,
				action: () => run(() => ExportMenuController.exportSPIDACalc(true, { silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportSPIDACalc(true, { silent }, selections)),
				testId: 'export-spidacalc-no-photo',
			},
			{
				visible: tagsSelected && ExportMenuController.hasPoleforeman(CurrentState.Tag.collections),
				labelKey: T.EXPORT_POLEFOREMAN,
				action: () => run(() => ExportMenuController.exportPoleforeman({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportPoleforeman({ silent }, selections)),
			},
			{
				visible: tagsSelected && ExportMenuController.hasIkePoleForeman(CurrentState.Tag.collections),
				labelKey: T.EXPORT_IKE_POLEFOREMAN,
				action: () => run(() => ExportMenuController.exportIkePoleForeman({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportIkePoleForeman({ silent }, selections)),
				id: 'export-ipf',
			},
			{
				visible: oneCollectionSelected,
				labelKey: T.EXPORT_IMAGE_ZIP,
				action: () => run(() => ExportMenuController.exportImageZip({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportImageZip({ silent }, selections)),
				testId: 'export-image-zip',
			},
			{
				visible: tagsSelected && ExportMenuController.hasDDS(CurrentState.Tag.collections),
				labelKey: T.EXPORT_DDS,
				action: () => run(() => ExportMenuController.exportDDS({ silent })),
				variantAction: (selections) => run(() => ExportMenuController.exportDDS({ silent }, selections)),
			},
		];
	}

	private viewDownloadItems(): MenuItem[] {
		return this.exportTypeEntries().map((entry) => ({
			visible: entry.visible,
			view: MenuItemComponent.view(Translate(entry.labelKey)),
			action: entry.action,
			id: entry.id,
			testId: entry.testId,
		}));
	}

	private visibleExportTypeOptions(): ExportTypeOption[] {
		return this.exportTypeEntries()
			.filter((entry) => entry.visible)
			.map((entry) => ({
				label: Translate(entry.labelKey),
				action: entry.action,
				variantAction: entry.variantAction,
				disableVariants: entry.disableVariants,
			}));
	}

	/**
	 * "Include variants" checkboxes for the current selection: "Existing" always first, then one
	 * per distinct `(variantType, variantTypeName)` pair actually present across the selected
	 * poles' variants — two variants sharing a type but differing in display name each get their
	 * own checkbox.
	 */
	private variantCheckboxOptions(): VariantCheckboxOption[] {
		const types = distinctVariantTypes(CurrentState.Tag.collections);
		return [
			{
				key: variantSelectionKey(EXISTING_VARIANT_GROUP),
				variantType: EXISTING_VARIANT_GROUP,
				label: Translate(T.VARIANT_TYPE_EXISTING),
			},
			...types.map((type) => ({
				key: variantSelectionKey(type.variantType, type.variantTypeName),
				variantType: type.variantType,
				variantTypeName: type.variantTypeName,
				label: type.variantTypeName || type.variantType,
			})),
		];
	}

	/**
	 * Builds the entry for a client-side PDF report, wiring both the plain path and the variant-aware
	 * path. Every report goes through here so none can be added without variant support — an entry
	 * without `variantAction` ignores the modal's checkboxes and exports the existing layer only.
	 */
	private reportEntry(type: ReportTypeEnum, labelKey: T, visible: boolean, testId: string): ExportTypeEntry {
		return {
			visible,
			labelKey,
			testId,
			action: () =>
				this.legacyMode
					? this.legacyExportPDF(type, labelKey)
					: ExportMenuController.runExport(() => this.exportPDF(type, labelKey)),
			variantAction: (selections) =>
				this.legacyMode
					? this.legacyExportPDFVariants(type, selections, labelKey)
					: ExportMenuController.runExport(() => this.exportPDFVariants(type, selections, labelKey)),
		};
	}

	// ── New export UI: PDF via download manager ──

	private async exportPDF(type: ReportTypeEnum, reportNameTranslationKey: T): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		const jobName = ExportMenuController.getExportLabel();
		const exportTypeLabel = Translate(reportNameTranslationKey);
		const taskId = taskManager.service.addFastExportTask(exportTypeLabel, jobName);

		try {
			const result = await Services.Department.rest.Department.User.isLicensed(null);
			if (!result.results) {
				Services.License.hasNoLicense = true;
				taskManager.service.failFastExportTask(taskId, undefined, 'License check failed');
				return;
			}

			const pdf = await ExportMenuController.exportReport(type);
			const fileName = `${Translate(reportNameTranslationKey)} ${new Date().toDateString()}.pdf`;

			const blob = await new Promise<Blob>((resolve, reject) => {
				try {
					pdf.getBlob((b: Blob) => resolve(b));
				} catch (err) {
					reject(err);
				}
			});
			const url = URL.createObjectURL(blob);
			taskManager.service.completeFastExportTask(taskId, url, fileName);
		} catch (_err) {
			taskManager.service.failFastExportTask(taskId, undefined, Translate(T.ERROR_GENERAL));
		}
	}

	/**
	 * Variant-aware counterpart to `exportPDF`: one PDF per selected "Include variants" group,
	 * zipped together by `ExportMenuController.exportReportVariants` when there's more than one
	 * (still merging every selected job into each variant's own PDF, exactly like the plain report).
	 */
	private async exportPDFVariants(
		type: ReportTypeEnum,
		selections: VariantExportSelection[],
		reportNameTranslationKey: T
	): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		const jobName = ExportMenuController.getExportLabel();
		const exportTypeLabel = Translate(reportNameTranslationKey);
		const taskId = taskManager.service.addFastExportTask(exportTypeLabel, jobName);

		try {
			const result = await Services.Department.rest.Department.User.isLicensed(null);
			if (!result.results) {
				Services.License.hasNoLicense = true;
				taskManager.service.failFastExportTask(taskId, undefined, 'License check failed');
				return;
			}

			const { blob, fileName } = await ExportMenuController.exportReportVariants(type, selections, exportTypeLabel);
			const url = URL.createObjectURL(blob);
			taskManager.service.completeFastExportTask(taskId, url, fileName);
		} catch (_err) {
			taskManager.service.failFastExportTask(taskId, undefined, Translate(T.ERROR_GENERAL));
		}
	}

	// ── Legacy export mode (no new-export-ui token) ──

	/**
	 * Legacy export orchestration: blocks the UI with a spinner on the export button, waits for
	 * the task(s) to complete, then auto-downloads or shows a notification modal for each. `func`
	 * returns more than one taskId when a single user action starts several separate downloads
	 * (e.g. one shapefile zip per selected variant) — each is awaited and handled independently.
	 */
	@action.bound
	private async runExportLegacy(func: () => Promise<string | string[] | void>): Promise<void> {
		this.exporting = true;
		const taskManager = getLongRunningTaskManager();
		taskManager.state.pendingExports++;
		try {
			const result = await ExportMenuController.runExport(func);
			if (!result) return;

			const taskIds = Array.isArray(result) ? result : [result];
			await Promise.all(taskIds.filter(Boolean).map((taskId) => this.handleLegacyTaskCompletion(taskId)));
		} catch (_) {
			NotificationService.error({ key: T.ERROR_GENERAL });
		} finally {
			taskManager.state.pendingExports--;
			this.exporting = false;
		}
	}

	private async handleLegacyTaskCompletion(taskId: string): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		const task = await taskManager.service.awaitTaskCompletion(taskId);

		// Mark as fully handled since legacy mode auto-handles everything
		const taskUpdate: Partial<LongRunningExport> = { seen: true, actioned: true, notified: true };
		taskManager.state.updateTask(task.taskId, taskUpdate);

		if (task.status === LongRunningTaskStatus.COMPLETED) {
			taskManager.service.downloadTask(task.taskId);
		} else if (task.status === LongRunningTaskStatus.FAILED) {
			await ModalService.open({
				title: Translate(T.NOTIFICATION_ERROR_TITLE),
				content: viewExportError(task.error || Translate(T.ERROR_EXPORT)),
			});
		} else {
			// COMPLETED_WARNINGS or COMPLETED_ERRORS — show notification modal
			await taskManager.service.showNotificationModal(task.taskId);
		}
	}

	/**
	 * Legacy PDF export: shows a modal with spinner, auto-downloads on completion.
	 * Matches the old production behavior from before the download manager UI.
	 */
	private async legacyExportPDF(type: ReportTypeEnum, reportNameTranslationKey: T): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		taskManager.state.pendingExports++;

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: Translate(T.EXPORTING_PDF_TITLE),
			content: m('div', {}, [
				m('div', { style: { marginBottom: DefaultTheme.space(2) } }, Translate(T.EXPORTING_PDF_CONTENT)),
				Spinner.view(),
			]),
			hasCancel: false,
		});

		try {
			const result = await Services.Department.rest.Department.User.isLicensed(null);
			if (result.results) {
				(await ExportMenuController.exportReport(type)).download(
					`${Translate(reportNameTranslationKey)} ${new Date().toDateString()}`,
					() => {
						taskManager.state.pendingExports--;
						ModalService.close();
					}
				);
			} else {
				Services.License.hasNoLicense = true;
				taskManager.state.pendingExports--;
				ModalService.close();
			}
		} catch (_err) {
			taskManager.state.pendingExports--;
			ModalService.close();
			NotificationService.error({ key: T.ERROR_GENERAL });
		}
	}

	/** Variant-aware counterpart to `legacyExportPDF` — same spinner modal, but downloads the PDF (or zip of PDFs) built by `exportReportVariants`. */
	private async legacyExportPDFVariants(
		type: ReportTypeEnum,
		selections: VariantExportSelection[],
		reportNameTranslationKey: T
	): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		taskManager.state.pendingExports++;

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: Translate(T.EXPORTING_PDF_TITLE),
			content: m('div', {}, [
				m('div', { style: { marginBottom: DefaultTheme.space(2) } }, Translate(T.EXPORTING_PDF_CONTENT)),
				Spinner.view(),
			]),
			hasCancel: false,
		});

		try {
			const result = await Services.Department.rest.Department.User.isLicensed(null);
			if (result.results) {
				const exportTypeLabel = Translate(reportNameTranslationKey);
				const { blob, fileName } = await ExportMenuController.exportReportVariants(type, selections, exportTypeLabel);
				const url = URL.createObjectURL(blob);
				Services.Download.download(url, fileName);
			} else {
				Services.License.hasNoLicense = true;
			}
		} catch (_err) {
			NotificationService.error({ key: T.ERROR_GENERAL });
		} finally {
			taskManager.state.pendingExports--;
			ModalService.close();
		}
	}
}
